<template>
  <div class="relative" :class="props.class">
    <InlineSelectBase
      v-bind="{ ...props, class: undefined }"
      v-model="selection"
      :class="showClear && modelValue != null && !disabled ? 'has-clear' : undefined"
      single
      @blur="emit('blur', $event)"
    >
      <template v-if="slots.value" #value>
        <slot name="value" :value="modelValue" :placeholder="placeholder" />
      </template>
      <template v-if="slots['option-label']" #option-label="{ option, label }">
        <slot name="option-label" :option="option" :label="label" />
      </template>
      <template v-if="slots['option-actions']" #option-actions="{ option, selected }">
        <slot name="option-actions" :option="option" :selected="selected" />
      </template>
      <template v-if="slots.footer" #footer>
        <slot name="footer" />
      </template>
    </InlineSelectBase>
    <button
      v-if="showClear && modelValue != null && !disabled"
      type="button"
      aria-label="Clear selection"
      class="absolute inset-y-0 right-8 flex w-8 items-center justify-center text-gray-900 hover:text-active-orange"
      @click="modelValue = undefined"
    >
      <XMarkIcon class="h-5 w-5" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts" generic="TOption extends Object, TValue = unknown">
import { computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import InlineSelectBase from './InlineSelectBase.vue'

const modelValue = defineModel<TValue>()
const props = defineProps<{
  inputId?: string
  options: TOption[]
  disabled?: boolean
  optionSearchText?: (option: TOption) => string
  optionLabel?: string | ((option: TOption) => string)
  optionValue?: string | ((option: TOption) => TValue)
  placeholder?: string
  optionsClass?: string
  appendToSelf?: boolean
  filter?: boolean
  filterPlaceholder?: string
  error?: string | null
  class?: string
  showClear?: boolean
  asNavigation?: boolean
}>()
const emit = defineEmits<{ blur: [event: Event] }>()
const slots = defineSlots<{
  value?: (props: { value: TValue | undefined; placeholder?: string }) => unknown
  'option-label'?: (props: { option: TOption; label: string }) => unknown
  'option-actions'?: (props: { option: TOption; selected: boolean }) => unknown
  footer?: () => unknown
}>()
const selection = computed({
  get: () =>
    modelValue.value === null || modelValue.value === undefined ? [] : [modelValue.value],
  set: (values: TValue[]) => {
    modelValue.value = values[0]
  }
})
</script>
